import { useState, useEffect } from 'react';
import { Menu, X, LogOut, User, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ThemeToggle } from '@/components/ThemeToggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { ProfileAvatar } from '@/components/ProfileAvatar';
import { supabase } from '@/integrations/supabase/client';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isLitigationPage = location.pathname === '/litigation';
  const shouldHideThemeToggle = isHomePage || isLitigationPage;
  const shouldUseWhiteNavColor = isHomePage && !isMobile;
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      setAvatarUrl(null);
      setUsername(null);
      return;
    }

    const fetchUnreadCount = async () => {
      try {
        const [messagesResult, notificationsResult] = await Promise.all([
          supabase
            .from('messages')
            .select('id', { count: 'exact', head: true })
            .eq('recipient_id', user.id)
            .eq('read', false),
          supabase
            .from('notifications')
            .select('id', { count: 'exact', head: true })
            .eq('recipient_id', user.id)
            .eq('read', false)
        ]);

        const totalUnread = (messagesResult.count || 0) + (notificationsResult.count || 0);
        setUnreadCount(totalUnread);
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };

    const fetchProfileData = async () => {
      try {
        const { data } = await supabase
          .from('public_profile_data')
          .select('avatar_url, username')
          .eq('id', user.id)
          .single();
        
        if (data) {
          setAvatarUrl(data.avatar_url);
          setUsername(data.username);
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    fetchUnreadCount();
    fetchProfileData();

    // Set up realtime subscription for updates
    const channel = supabase
      .channel('unread-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `recipient_id=eq.${user.id}`
        },
        () => fetchUnreadCount()
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${user.id}`
        },
        () => fetchUnreadCount()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const navItems = [
    { name: 'About', href: '/about' },
    { name: 'Maxims of Law', href: '/maxims-of-law' },
    { name: 'Services', href: '/services' },
    { name: 'Dictionary', href: '/dictionary' },
    { name: 'Resources & Learning', href: '/resources-learning' },
    { name: 'FAQ', href: '/faq' },
    { name: 'Contact', href: '/work-with-us#contact' },
  ];

  return (
    <>
      {/* Blur backdrop for mobile menu */}
      {isMenuOpen && isMobile && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
          onClick={() => setIsMenuOpen(false)}
        />
      )}
      
      <header className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isMenuOpen ? 'bg-white/80 backdrop-blur-md' : 'bg-background/20 backdrop-blur-md'
      }`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <img 
                  src="/lovable-uploads/5d5f7aaa-1fac-4dc4-b133-17fc214f154b.png" 
                  alt="Freeman & Crown Logo" 
                  className="h-12 w-auto opacity-0 animate-fade-in"
                  style={{ animationDelay: '0.8s', animationFillMode: 'forwards' }}
                />
                </Link>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-12">
              {navItems.map((item, index) => (
                <Link 
                  key={item.name}
                  to={item.href}
                  className={`${shouldUseWhiteNavColor ? 'text-white' : 'text-foreground'} hover:text-black transition-colors duration-300 font-medium font-rockwell uppercase text-sm opacity-0 animate-fade-in`}
                  style={{ animationDelay: `${0.7 + index * 0.1}s`, animationFillMode: 'forwards' }}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-4 opacity-0 animate-fade-in" style={{ animationDelay: '0.9s', animationFillMode: 'forwards' }}>
              {!shouldHideThemeToggle && <ThemeToggle />}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="relative rounded-full hover:bg-transparent">
                      <ProfileAvatar
                        src={avatarUrl}
                        alt={username || 'User'}
                        fallback={username?.[0]?.toUpperCase() || 'U'}
                        className="h-8 w-8"
                      />
                      {unreadCount > 0 && (
                        <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 min-w-[20px] flex items-center justify-center px-1.5 text-xs">
                          {unreadCount}
                        </Badge>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-background border">
                    <DropdownMenuItem onClick={() => navigate('/profile')}>
                      <User className="mr-2 h-4 w-4" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/inbox')}>
                      <MessageCircle className="mr-2 h-4 w-4" />
                      Inbox
                      {unreadCount > 0 && (
                        <Badge variant="destructive" className="ml-auto h-5 min-w-[20px] flex items-center justify-center px-1.5 text-xs">
                          {unreadCount}
                        </Badge>
                      )}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
              <Button variant="outline" asChild>
                <Link to="/auth">Login</Link>
              </Button>
              )}
              <Button className="bg-black text-white hover:bg-black/90 dark:bg-white dark:text-black dark:hover:bg-white/90 font-rockwell" asChild>
                <Link to="/work-with-us">Work with us</Link>
              </Button>
            </div>

            {/* Mobile Menu Button and Controls */}
            <div className="md:hidden flex items-center gap-3">
              {!shouldHideThemeToggle && <ThemeToggle />}
              {user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="w-10 h-10 relative rounded-full hover:bg-transparent">
                      <ProfileAvatar
                        src={avatarUrl}
                        alt={username || 'User'}
                        fallback={username?.[0]?.toUpperCase() || 'U'}
                        className="h-8 w-8"
                      />
                      {unreadCount > 0 && (
                        <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 min-w-[20px] flex items-center justify-center px-1.5 text-xs">
                          {unreadCount}
                        </Badge>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-background border w-56">
                    <DropdownMenuItem onClick={() => {
                      navigate('/profile');
                      setIsMenuOpen(false);
                    }}>
                      <User className="mr-2 h-4 w-4" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => {
                      navigate('/inbox');
                      setIsMenuOpen(false);
                    }}>
                      <MessageCircle className="mr-2 h-4 w-4" />
                      Inbox
                      {unreadCount > 0 && (
                        <Badge variant="destructive" className="ml-auto h-5 min-w-[20px] flex items-center justify-center px-1.5 text-xs">
                          {unreadCount}
                        </Badge>
                      )}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <button
                className={`${isHomePage && isMobile ? 'text-white' : 'text-foreground'} flex flex-col items-end justify-center gap-1 w-6 h-6`}
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Menu"
              >
                {isMenuOpen ? (
                  <X size={24} />
                ) : (
                  <>
                    <span className="w-6 h-0.5 bg-current transition-all" />
                    <span className="w-4 h-0.5 bg-current transition-all" />
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 border-t border-border pt-4">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="block py-2 dark:text-black text-foreground hover:text-black transition-colors duration-300 font-rockwell uppercase"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              {!user && (
              <Button className="w-full mt-4" variant="outline" asChild>
                <Link to="/auth" onClick={() => setIsMenuOpen(false)}>Login</Link>
              </Button>
              )}
              <Button className="w-full mt-2 bg-black text-white hover:bg-black/90 font-rockwell" asChild>
                <Link to="/work-with-us" onClick={() => setIsMenuOpen(false)}>Work with us</Link>
              </Button>
            </nav>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
